/**
 * OKS-738 — random crew-perk tagline picker for the invite brand splash.
 *
 * The invite splash (`<BrandSplash>` via `InviteLanding`) cross-fades
 * through a small, randomly-chosen subset of a static tagline pool during
 * its ~3.5s hold. The random pick lives HERE — a pure function — rather
 * than inside `<BrandSplash>`, so the component stays deterministic given
 * its `taglines` prop (UX Spec §1 / TRD KD-4). `InviteLanding` calls this
 * once on mount and passes the ordered result down.
 *
 * Guarantees:
 *   - Returns `count` **distinct** entries (default: a random 2 or 3).
 *   - `count` is clamped to `[0, pool.length]` — never over-picks a short
 *     pool, never returns duplicates.
 *   - Preserves relative order of the picked entries as a stable,
 *     shuffle-free read (partial Fisher–Yates over indices, then sorted)
 *     is NOT used — instead we shuffle and slice, so order is randomized
 *     too (variety by design). Distinctness is the invariant tests rely on.
 *
 * Testability: `rng` is injectable (defaults to `Math.random`). Passing a
 * deterministic/seeded `rng` makes the pick fully reproducible in Vitest —
 * no reliance on real `Math.random`. `Math.random()` in app code is fine
 * (ticket note); only the pool selection needs to be seedable.
 *
 * @param pool  the full tagline pool (e.g. the 6 i18n strings)
 * @param opts.count  how many to pick; default random 2 or 3
 * @param opts.rng    0..1 generator; default `Math.random`
 * @returns up to `count` distinct taglines, in randomized order
 */
export interface PickTaglinesOptions {
  count?: number
  rng?: () => number
}

export function pickTaglines(
  pool: string[],
  opts: PickTaglinesOptions = {},
): string[] {
  const { rng = Math.random } = opts

  // Default count: a random 2 or 3 (variety by design — some sessions
  // show 2, some 3, per PRD §3 / UX Spec §9).
  const requested = opts.count ?? (rng() < 0.5 ? 2 : 3)

  // Clamp to a sane, pool-bounded integer count.
  const count = Math.max(0, Math.min(Math.floor(requested), pool.length))
  if (count === 0) return []

  // Fisher–Yates shuffle over a copy, then take the first `count` — this
  // yields `count` DISTINCT entries in randomized order without mutating
  // the caller's array.
  const shuffled = [...pool]
  for (let i = shuffled.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rng() * (i + 1))
    ;[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
  }

  return shuffled.slice(0, count)
}
